// Çağrı akışının istemci tarafı durum makinesi (9.4.5.2'nin sunucudaki
// kaydının aynadaki hâli).
//
// Neden yoklama: gerçek zamanlı kanal henüz yok (10.1 açık) ve ilk dilim
// bilinçli olarak soketsiz kuruldu (9.4.5.1). Arayan cevabı, aranan çağrının
// kapanmasını yoklayarak öğreniyor. Gelen-arama push'u cihazı sesli uyarır;
// SDP ve kesin çağrı durumunun kaynağı yine bu dar yoklamadır. Gerçek zamanlı
// kanal geldiğinde bu dosyadaki `POLL_MS` anlamını yitirecek.
//
// Kalan süre **sunucudan** okunuyor (9.4.3.1): cihaz saatine güvenilseydi
// saat oynatılarak bedava konuşulabilirdi. Ekrandaki sayaç yalnızca durum
// yoklamaları arasını yumuşatmak için yerel olarak ilerliyor.
import { useCallback, useEffect, useRef, useState } from 'react';
import { AppState, type AppStateStatus } from 'react-native';
import { api, ApiError, NetworkError, type Call, type IncomingCall } from '../services/api';
import { createCallMedia, type CallMedia } from './media';
import {
  MEDIA_INITIAL_CONNECT_TIMEOUT_MS,
  MEDIA_INTERRUPT_GRACE_MS,
  MEDIA_RECONNECT_DELAYS_MS,
  type CallStreams,
  type MediaConnectionState,
} from './types';
import {
  audioRoutingAvailable,
  observeCallAudioRoutes,
  selectCallAudioRoute,
  startCallAudioSession,
  stopCallAudioSession,
  type AudioRoute,
} from './audioRoute';
import {
  answerSystemCall,
  endSystemCall,
  getPendingSystemCallAction,
  observeSystemCallActions,
  reportSystemIncomingCall,
  systemCallingAvailable,
  type SystemCallAction,
} from './systemCall';

// Push artık gelen çağrının birincil uyandırma katmanı. Ön plandaki seyrek
// yoklama, push teslim edilemezse çağrıyı yine bulabilen bir emniyet ağıdır;
// arka planda native sistem çağrısı/push varken boşuna HTTP üretmez.
const INCOMING_FALLBACK_POLL_MS = 10000;
const INCOMING_STATUS_POLL_MS = 2000;
const OUTGOING_POLL_MS = 2000;
const ACTIVE_POLL_MS = 3000;

export type CallPhase = 'idle' | 'outgoing' | 'incoming' | 'active' | 'ended';

export function callStatusPollDelay(phase: CallPhase): number | null {
  if (phase === 'outgoing') return OUTGOING_POLL_MS;
  if (phase === 'active') return ACTIVE_POLL_MS;
  return null;
}

export function shouldPollIncoming(appState: AppStateStatus, phase: CallPhase): boolean {
  return appState === 'active' && phase === 'idle';
}

export function callStartErrorTranslationKey(error: unknown): string {
  if (!(error instanceof ApiError)) return 'call.failed';
  switch (error.code) {
    case 'CALL_START_INSUFFICIENT_BALANCE': return 'call.insufficient';
    case 'CALL_START_BUSY': return 'call.busy';
    case 'CALL_START_MESSAGE_REQUIRED': return 'call.notAllowed';
    case 'CALL_START_RATE_LIMITED': return 'call.rateLimited';
    case 'RESERVATION_CALL_LINK_REJECTED': return 'call.reservationLinkUnavailable';
    // 9.4.7.5 — alıcının "kimler beni arayabilir" tercihi reddetti (D-226).
    // Genel "başlatılamadı" yerine nedenini söylemek gerekiyor; kesin seçim
    // (kimse mi, yalnız bağlantılar mı) alıcının mahremiyeti olduğu için
    // arayana tek ve nötr bir metinle bildirilir.
    case 'CALL_START_NOT_ALLOWED': return 'call.notAccepting';
    // 3.10.5 — gösterilen dakika ücreti artık geçerli değil. Para hiç hareket
    // etmedi; kullanıcı güncel ücreti görüp bilerek tekrar arar. Mesajdaki gibi
    // sessiz yeniden deneme yapılmaz: kaybolacak bir taslak yok ve çağrıyı
    // kullanıcının onayı olmadan yeni ücretten açmak damganın amacına aykırı.
    case 'price_changed': return 'call.priceChanged';
    case 'CALL_START_FAILED':
    default: return 'call.failed';
  }
}

export function incomingCallDeadlineDelay(createdAt: string, now = Date.now()): number {
  const created = Date.parse(createdAt);
  if (!Number.isFinite(created)) return 45_000;
  return Math.max(0, created + 45_000 - now);
}

export function initialMediaConnectDelay(startedAt: number, now = Date.now()): number {
  return Math.max(0, startedAt + MEDIA_INITIAL_CONNECT_TIMEOUT_MS - now);
}

export function createCallStartRequestId(
  now: () => number = Date.now,
  random: () => number = Math.random,
): string {
  const entropy = () => Math.floor(random() * 0x1_0000_0000)
    .toString(36)
    .padStart(7, '0');
  return `call_${now().toString(36)}_${entropy()}${entropy()}`;
}

export type CallState = {
  phase: CallPhase;
  call: Call | null;
  incoming: IncomingCall | null;
  /** Kabul anından beri geçen saniye — ücretin işlediği süre (9.4.2.3). */
  elapsed: number;
  /** Sunucunun bildirdiği kalan saniye; rezervasyon tükendiğinde çağrı kesilir. */
  remaining: number | null;
  /** Karşı tarafa bakiye/saniye açıklamadan çağrının yakında biteceğini söyler. */
  endingSoon: boolean;
  muted: boolean;
  /** Native iletişim ses yolunun güncel ve seçilebilir uçları (9.4.11.2/.3). */
  audioRoute: AudioRoute | null;
  audioRoutes: AudioRoute[];
  audioRoutingAvailable: boolean;
  audioRouteError: boolean;
  /** D-418 — kullanıcı Bluetooth iznini reddetti; yönlendirme hiç denenmedi. */
  bluetoothPermissionDenied: boolean;
  /** Medya bağlantısının durumu (9.4.4.5). Sunucu ücreti duvar saatinden
   *  hesapladığı için kopmayı yalnız istemci görür. */
  media: MediaConnectionState;
  /** Görüntülü çağrı mı (9.4.14). Sesli çağrıda kamera hiç istenmez. */
  video: boolean;
  /** Çizilecek akışlar; görüntü kapalıyken ikisi de kullanılmaz. */
  streams: CallStreams;
  /** Kamera geçici olarak kapalı mı (9.4.14). Ses ve çağrı sürer. */
  cameraOff: boolean;
  /** Kapanış özeti izleyenin rolüne göre kesilen veya kazanılan net tutardır. */
  summary: {
    seconds: number;
    amount: number;
    amountKind: 'spent' | 'earned';
    currency: string | null;
  } | null;
  error: string | null;
  errorTranslationKey: string | null;
};

const INITIAL: CallState = {
  phase: 'idle', call: null, incoming: null, elapsed: 0,
  remaining: null, endingSoon: false, muted: false, audioRoute: null, audioRoutes: [],
  audioRoutingAvailable: false, audioRouteError: false, bluetoothPermissionDenied: false,
  media: 'connecting', video: false, streams: { local: null, remote: null }, cameraOff: false,
  summary: null, error: null, errorTranslationKey: null,
};

export function advanceVisibleCallClock(state: CallState): CallState {
  if (state.media !== 'connected') return state;
  const elapsed = state.elapsed + 1;
  const remaining = state.remaining === null ? null : Math.max(0, state.remaining - 1);
  return {
    ...state,
    elapsed,
    remaining,
    endingSoon: state.endingSoon || (remaining !== null && remaining <= 60),
  };
}

export function useCall(token: string | null) {
  const [state, setState] = useState<CallState>(INITIAL);
  const mediaRef = useRef<CallMedia | null>(null);
  // Native olay, React durum geçişinden önce gelebilir. Ref kesin medya
  // gerçeğini korur; böylece kabul yanıtı bağlantı olayını ezmez.
  const mediaStateRef = useRef<MediaConnectionState>('connecting');
  const mediaEverConnectedRef = useRef(false);
  const initialMediaCallRef = useRef<number | null>(null);
  const initialMediaWaitStartRef = useRef<number | null>(null);
  // Yoklama döngüleri kapanmış bir çağrıyı güncellemeye devam etmesin diye
  // aktif çağrının kimliği ref'te tutuluyor.
  const activeId = useRef<number | null>(null);
  // Görüntü tercihi medya kurulurken okunur; render döngüsüne bağlı olmamalı.
  const videoRef = useRef(false);
  // 9.4.4.5 — yeniden anlaşmada rol: ICE-restart offer'ını DAİMA arayan üretir
  // (tek offerer → glare yok). start() 'caller', accept() 'callee' yazar.
  const roleRef = useRef<'caller' | 'callee' | null>(null);
  // Uygulanan son yeniden anlaşma turu; aynı offer/answer iki kez uygulanmasın.
  const renegSeqRef = useRef(0);
  const pendingSystemActionRef = useRef<SystemCallAction | null>(null);
  // Aynı kullanıcı eylemi iki UI/native kaynaktan aynı anda gelebilir. İstemci
  // kapıları UX'i tek-geçişli tutar; para güvenliğinin asıl kapısı sunucudadır.
  const startAttemptRef = useRef(0);
  const startInFlightRef = useRef(false);
  const incomingActionRef = useRef<{ id: number; action: 'accept' | 'decline' } | null>(null);
  // Native sistem yüzeyi çağrıyı cevapladıysa JS aynı native cevabı ikinci kez
  // göndermemeli. Üretilmiş WebRTC cevabı da API yanıtı kaybolduğunda durum
  // yoklamasıyla kurtarılabilmek için çağrı yaşam döngüsü boyunca korunur.
  const systemAnsweredCallRef = useRef<number | null>(null);
  const incomingAnswerRef = useRef<{ id: number; answer: string } | null>(null);
  const endingCallRef = useRef<number | null>(null);
  // Kopukluk ölçümü çağrı yaşam döngüsüne aittir. Her teardown'da sıfırlanır;
  // eski bir çağrının ölçümü sonraki çağrıya taşınmamalıdır.
  const kopukBasi = useRef<number | null>(null);
  // Sunucuya delta değil çağrı-kapsamlı toplam gönderilir. Böylece bildirim
  // yanıtı kaybolduğunda aynı toplam yeniden denenebilir ve kapanış isteği
  // henüz ulaşmamış son kesintiyi aynı transaction'a taşıyabilir.
  const interruptionTotalRef = useRef(0);
  // Push yaşam döngüsü, aynı kesin GET isteğini beklemeden tetikleyebilsin.
  // Fonksiyon yalnız `idle` etkisi ayaktayken doludur.
  const refreshIncomingRef = useRef<(() => void) | null>(null);
  const refreshIncoming = useCallback(() => { refreshIncomingRef.current?.(); }, []);

  const teardown = useCallback(() => {
    const callId = activeId.current;
    if (callId !== null) endSystemCall(callId).catch(() => {});
    mediaRef.current?.close();
    mediaRef.current = null;
    mediaStateRef.current = 'connecting';
    mediaEverConnectedRef.current = false;
    initialMediaCallRef.current = null;
    initialMediaWaitStartRef.current = null;
    stopCallAudioSession().catch(() => {});
    activeId.current = null;
    roleRef.current = null;
    renegSeqRef.current = 0;
    kopukBasi.current = null;
    interruptionTotalRef.current = 0;
    incomingActionRef.current = null;
    endingCallRef.current = null;
    systemAnsweredCallRef.current = null;
    incomingAnswerRef.current = null;
  }, []);

  const reset = useCallback(() => {
    // Bekleyen bir başlangıç sunucudan geç dönerse aşağıdaki attempt kimliği
    // onu yeni çağrı gibi diriltmez; oluşturulmuşsa hold'u /end ile bırakır.
    startAttemptRef.current += 1;
    startInFlightRef.current = false;
    incomingActionRef.current = null;
    endingCallRef.current = null;
    teardown();
    setState(INITIAL);
  }, [teardown]);

  const media = useCallback(async () => {
    if (mediaRef.current) return mediaRef.current;
    const { iceServers } = await api.getIceServers(token as string);
    // İletişim ses oturumu mikrofondan önce açılır; böylece WebRTC ilk anda
    // medya/çalma kategorisinde değil, iki yönlü konuşma yolunda başlar.
    if (audioRoutingAvailable) {
      try {
        const route = await startCallAudioSession();
        if (route) {
          setState((st) => ({
            ...st,
            audioRoute: route.current,
            audioRoutes: route.available,
            audioRoutingAvailable: true,
          }));
        }
      } catch {
        // Ses yolu yardımcısının bozulması çağrıyı kesmez; çalışmayan kontrol
        // gösterilmez ve WebRTC işletim sisteminin varsayılan yoluyla sürer.
      }
    }
    // Görüntü seçeneği medya kurulurken belli olmalı: kamera teklif üretilmeden
    // önce akışa eklenir, sonradan açılamaz (9.4.14).
    const m = createCallMedia(iceServers, { video: videoRef.current });
    m.onStreamChange((streams) => {
      setState((st) => (st.phase === 'idle' ? st : { ...st, streams }));
    });
    // 9.4.4.5 — kopmayı izle. Sunucu medyayı görmediği için çağrıyı bitirme
    // sorumluluğu istemcide: aksi hâlde bağlantı ölse bile çağrı rezerve bakiye
    // tükenene kadar sürer ve tamamı ücretlendirilir.
    m.onConnectionChange((durum) => {
      mediaStateRef.current = durum;
      if (durum === 'connected') mediaEverConnectedRef.current = true;
      setState((st) => (st.phase === 'active' || st.phase === 'outgoing'
        ? { ...st, media: durum }
        : st));
    });
    mediaRef.current = m;
    return m;
  }, [token]);

  // ── Giden çağrı ────────────────────────────────────────────────────────
  // `reservationId` — 9.31.8: çağrı planlı bir rezervasyondan doğuyorsa
  // sunucuya bildirilir ve o rezervasyona bağlanır.
  const start = useCallback(async (
    calleeId: number,
    reservationId?: number | null,
    priceStamp?: { amount: number; currency: string } | null,
    /** 9.4.14 — görüntülü başlat. Kamera medya kurulurken açılır. */
    video = false
  ) => {
    if (!token || startInFlightRef.current) return;
    const attempt = startAttemptRef.current + 1;
    startAttemptRef.current = attempt;
    startInFlightRef.current = true;
    const isCurrent = () => startAttemptRef.current === attempt;
    roleRef.current = 'caller';
    renegSeqRef.current = 0;
    // Kamera tercihi medya kurulmadan ÖNCE yazılmalı: track teklif üretilirken
    // ekleniyor, sonradan açılamıyor (9.4.14).
    videoRef.current = video;
    setState({ ...INITIAL, phase: 'outgoing', video });
    try {
      const m = await media();
      if (!isCurrent()) return;
      const offer = await m.createOffer();
      if (!isCurrent()) return;
      const clientRequestId = createCallStartRequestId();
      let call: Call;
      try {
        call = await api.startCall(token, calleeId, offer, clientRequestId, reservationId, priceStamp,
          video ? 'video' : 'audio');
      } catch (error) {
        // İlk istek sunucuda zil + hold oluşturmuş fakat yanıt ağda kaybolmuş
        // olabilir. Yalnız ağ hatasını aynı anahtarla bir kez tekrarlarız;
        // sunucu aynı çağrıyı döndürür, yeni zil veya ikinci hold açmaz.
        if (!(error instanceof NetworkError) || !isCurrent()) throw error;
        call = await api.startCall(token, calleeId, offer, clientRequestId, reservationId, priceStamp,
          video ? 'video' : 'audio');
      }
      if (!isCurrent()) {
        // Kullanıcı sunucu yanıtından önce vazgeçmiş olabilir. İşlem sunucuda
        // oluştuysa rezervasyonu açık bırakma; /end idempotent mahsuplaştırır.
        await api.endCall(token, call.id).catch(() => {});
        return;
      }
      activeId.current = call.id;
      setState((s) => ({ ...s, call }));
    } catch (e) {
      if (!isCurrent()) return;
      teardown();
      setState({
        ...INITIAL,
        phase: 'ended',
        error: null,
        errorTranslationKey: callStartErrorTranslationKey(e),
      });
    } finally {
      if (isCurrent()) startInFlightRef.current = false;
    }
  }, [token, media, teardown]);

  // ── Gelen çağrı yoklaması (push uyandırır, kesin durum buradan gelir) ──
  useEffect(() => {
    if (!token || state.phase !== 'idle') return;
    let stopped = false;
    let inFlight = false;
    let appState: AppStateStatus = AppState.currentState ?? 'active';
    let timer: ReturnType<typeof setTimeout> | null = null;

    const clearTimer = () => {
      if (timer !== null) clearTimeout(timer);
      timer = null;
    };
    const schedule = () => {
      clearTimer();
      if (!stopped && shouldPollIncoming(appState, state.phase)) {
        timer = setTimeout(() => { void tick(); }, INCOMING_FALLBACK_POLL_MS);
      }
    };
    const tick = async () => {
      if (stopped || inFlight || !shouldPollIncoming(appState, state.phase)) return;
      inFlight = true;
      try {
        const { call } = await api.getIncomingCall(token);
        if (!stopped && call) {
          activeId.current = call.id;
          setState({ ...INITIAL, phase: 'incoming', incoming: call });
        }
      } catch { /* ağ hatası yoklamayı durdurmaz */ }
      finally {
        inFlight = false;
        if (!activeId.current) schedule();
      }
    };
    const runNow = () => {
      clearTimer();
      void tick();
    };
    const onAppState = (next: AppStateStatus) => {
      appState = next;
      if (shouldPollIncoming(next, state.phase)) runNow();
      else clearTimer();
    };

    refreshIncomingRef.current = runNow;
    const subscription = AppState.addEventListener('change', onAppState);
    if (shouldPollIncoming(appState, state.phase)) runNow();
    return () => {
      stopped = true;
      clearTimer();
      subscription.remove();
      if (refreshIncomingRef.current === runNow) refreshIncomingRef.current = null;
    };
  }, [token, state.phase]);

  // Gelen çağrı ekrana alındığında boşta-yoklama durur. Bu dar durum yoklaması
  // arayanın vazgeçmesini, başka cihazdan reddi ve sunucunun çalma süresi sonunu
  // öğrenir; alıcının eski çağrı ekranında kalmasını önler.
  useEffect(() => {
    if (!token || state.phase !== 'incoming' || !state.incoming) return;
    const id = state.incoming.id;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const deadlineTimer = setTimeout(() => {
      // Kabul/ret başlamışsa kesin sonucu o eylemin sunucu yanıtı belirler.
      if (stopped || incomingActionRef.current) return;
      teardown();
      setState((s) => ({ ...s, phase: 'ended', incoming: null }));
    }, incomingCallDeadlineDelay(state.incoming.created_at));

    const tick = async () => {
      if (stopped) return;
      try {
        const call = await api.getCall(token, id);
        if (stopped) return;
        if (call.status === 'accepted') {
          // Bu cihaz kabulü başlattıysa POST yanıtı kaybolmuş olabilir; GET
          // sunucu gerçeğini taşıyarak aktif ekrana geçirir. Başka cihaz kabul
          // ettiyse bu cihaz yalnız çalmayı ve eski ekranı kapatır.
          if (incomingAnswerRef.current?.id === id) {
            incomingActionRef.current = null;
            setState((s) => ({
              ...s,
              phase: 'active',
              call,
              incoming: null,
              elapsed: call.elapsed_seconds ?? s.elapsed,
              remaining: call.remaining_seconds,
              endingSoon: call.ending_soon,
              media: mediaStateRef.current,
              error: null,
              errorTranslationKey: null,
            }));
          } else {
            teardown();
            setState(INITIAL);
          }
          return;
        }
        if (call.status === 'ended' || call.status === 'declined'
          || call.status === 'cancelled' || call.status === 'missed') {
          teardown();
          setState((s) => ({ ...s, phase: 'ended', call, incoming: null }));
          return;
        }
      } catch { /* geçici ağ hatası bir sonraki yoklamada yeniden denenir */ }
      if (!stopped) timer = setTimeout(() => { void tick(); }, INCOMING_STATUS_POLL_MS);
    };

    void tick();
    return () => {
      stopped = true;
      if (timer !== null) clearTimeout(timer);
      clearTimeout(deadlineTimer);
    };
  }, [token, state.phase, state.incoming, teardown]);

  const accept = useCallback(async (answeredBySystem = false) => {
    if (!token || !state.incoming) return;
    const incomingId = state.incoming.id;
    if (incomingActionRef.current) return;
    incomingActionRef.current = { id: incomingId, action: 'accept' };
    const ownsAction = () => incomingActionRef.current?.id === incomingId
      && incomingActionRef.current.action === 'accept';
    roleRef.current = 'callee';
    renegSeqRef.current = 0;
    // 9.4.14 — aranan taraf kamerayı ancak arayan görüntülü aradıysa açar.
    // Tür sunucudan gelir; istemci kendi başına karar veremez, yoksa bir taraf
    // görüntü gönderirken öbürü göndermez ve ekran tek yönlü kalır.
    const gorusmeliMi = state.incoming.kind === 'video';
    videoRef.current = gorusmeliMi;
    setState((st) => ({ ...st, video: gorusmeliMi }));
    try {
      if (answeredBySystem) systemAnsweredCallRef.current = incomingId;
      if (systemAnsweredCallRef.current !== incomingId) {
        await answerSystemCall(incomingId);
        systemAnsweredCallRef.current = incomingId;
      }
      if (!ownsAction()) return;
      const m = await media();
      if (!ownsAction()) return;
      let answer = incomingAnswerRef.current?.id === incomingId
        ? incomingAnswerRef.current.answer
        : null;
      if (!answer) {
        answer = await m.acceptOffer(state.incoming.offer_sdp ?? '');
        incomingAnswerRef.current = { id: incomingId, answer };
      }
      if (!ownsAction()) return;
      const call = await api.acceptCall(token, incomingId, answer);
      if (!ownsAction()) return;
      incomingActionRef.current = null;
      setState((s) => ({
        ...s,
        phase: 'active',
        call,
        incoming: null,
        elapsed: call.elapsed_seconds ?? 0,
        media: mediaStateRef.current,
        error: null,
        errorTranslationKey: null,
      }));
    } catch {
      if (!ownsAction()) return;
      incomingActionRef.current = null;
      // SDP cevabı üretilemediyse bozuk peer'ı bir sonraki deneme için yenile.
      // Cevap üretildiyse POST yanıtı kaybolmuş olabilir; medya ve cevap,
      // iki saniyelik sunucu uzlaştırmasının accepted durumunu bulması için kalır.
      if (incomingAnswerRef.current?.id !== incomingId) {
        mediaRef.current?.close();
        mediaRef.current = null;
        stopCallAudioSession().catch(() => {});
      }
      setState((s) => ({ ...s, errorTranslationKey: 'call.acceptFailed' }));
    }
  }, [token, state.incoming, media]);

  const decline = useCallback(async () => {
    if (!token || !state.incoming) return;
    const incomingId = state.incoming.id;
    if (incomingActionRef.current) return;
    incomingActionRef.current = { id: incomingId, action: 'decline' };
    try {
      await api.declineCall(token, incomingId);
    } catch {
      if (incomingActionRef.current?.id === incomingId) {
        incomingActionRef.current = null;
        setState((s) => ({ ...s, errorTranslationKey: 'call.declineFailed' }));
      }
      return;
    }
    await endSystemCall(incomingId).catch(() => {});
    reset();
  }, [token, state.incoming, reset]);

  const kesintiyiToplamaEkle = useCallback(() => {
    const bas = kopukBasi.current;
    kopukBasi.current = null;
    if (bas === null) return interruptionTotalRef.current;
    const saniye = Math.round((Date.now() - bas) / 1000);
    if (saniye >= 1) interruptionTotalRef.current += saniye;
    return interruptionTotalRef.current;
  }, []);

  // Kopukluk kapandığında toplamı bildirir. Ağ yanıtı kaybolursa toplam ref'te
  // kalır; sonraki kesinti veya /end aynı değeri güvenle yeniden gönderir.
  const bildirKopukluk = useCallback(async () => {
    const id = activeId.current;
    const toplam = kesintiyiToplamaEkle();
    if (toplam < 1 || id === null || !token) return;
    try { await api.reportCallInterruption(token, id, toplam); } catch { /* sonraki gönderim dener */ }
  }, [token, kesintiyiToplamaEkle]);

  const finishCall = useCallback(async (reason?: 'media_not_connected') => {
    if (endingCallRef.current !== null) return;
    const id = activeId.current;
    // Arama API yanıtı henüz dönmediyse reset attempt kimliğini geçersiz kılar;
    // sunucuda çağrı daha sonra oluşursa start() onu ayrıca kapatır.
    if (state.phase === 'outgoing' && startInFlightRef.current && id === null) {
      reset();
      return;
    }
    if (!token || id === null) { reset(); return; }
    endingCallRef.current = id;
    setState((s) => ({ ...s, error: null, errorTranslationKey: null }));
    try {
      // Son kopukluğu istemcideki kümülatif toplama alıp /end gövdesine koymak,
      // ayrı /interruption isteği düşse veya kapanış onu geçse bile mahsuplaşmayı
      // aynı satır kilidi ve transaction içinde doğru tutar.
      const interruptionTotal = reason === 'media_not_connected'
        ? 0
        : kesintiyiToplamaEkle();
      const r = interruptionTotal > 0
        ? await api.endCall(token, id, reason, interruptionTotal)
        : await api.endCall(token, id, reason);
      const amountKind = state.call?.viewer_amount_kind
        ?? (roleRef.current === 'callee' ? 'earned' : 'spent');
      const amount = amountKind === 'earned' ? r.receiverAmount : r.chargedAmount;
      const currency = amountKind === 'earned'
        ? state.call?.price_currency ?? null
        : state.call?.payer_currency ?? null;
      teardown();
      setState((s) => ({
        ...s,
        phase: 'ended',
        summary: { seconds: r.seconds, amount, amountKind, currency },
        errorTranslationKey: reason === 'media_not_connected'
          ? 'call.mediaConnectionFailed'
          : null,
      }));
    } catch {
      // İstek sunucuya ulaşıp yalnız yanıt kaybolmuş olabilir. Aktif/giden
      // ekranı koru; durum yoklaması terminal gerçeği bulur veya kullanıcı
      // aynı idempotent sonlandırmayı yeniden deneyebilir.
      if (activeId.current !== id) return;
      if (endingCallRef.current === id) endingCallRef.current = null;
      setState((s) => ({ ...s, errorTranslationKey: 'call.endFailed' }));
    }
  }, [
    token,
    state.phase,
    state.call?.viewer_amount_kind,
    state.call?.payer_currency,
    state.call?.price_currency,
    reset,
    teardown,
    kesintiyiToplamaEkle,
  ]);

  const hangUp = useCallback(async () => finishCall(), [finishCall]);

  // Android Core-Telecom/CallStyle eylemleri native tarafta doğar. JS köprüsü
  // soğuk açılıştan sonra hazır olabildiği için eylem önce kalıcı kuyruğa
  // düşer; kesin çağrı ve SDP yine sunucudan okunduktan sonra uygulanır.
  useEffect(() => {
    if (!systemCallingAvailable) return;
    const apply = (action: SystemCallAction) => {
      const currentId = state.incoming?.id ?? state.call?.id ?? null;
      if (currentId !== action.callId) {
        pendingSystemActionRef.current = action;
        return;
      }
      if (action.action === 'answer' && state.phase === 'incoming') {
        pendingSystemActionRef.current = null;
        void accept(true);
      } else if (action.action === 'decline' && state.phase === 'incoming') {
        pendingSystemActionRef.current = null;
        void decline();
      } else if (action.action === 'end' && state.phase === 'active') {
        pendingSystemActionRef.current = null;
        void hangUp();
      } else {
        pendingSystemActionRef.current = action;
      }
    };
    getPendingSystemCallAction().then(action => { if (action) apply(action); }).catch(() => {});
    return observeSystemCallActions(apply);
  }, [state.phase, state.incoming, state.call?.id, accept, decline, hangUp]);

  useEffect(() => {
    if (!systemCallingAvailable || state.phase !== 'incoming' || !state.incoming) return;
    const displayName = [state.incoming.caller_name, state.incoming.caller_surname]
      .filter(Boolean)
      .join(' ') || 'nosubject';
    const createdAt = Date.parse(state.incoming.created_at);
    const expiresAt = Number.isFinite(createdAt) ? createdAt + 45_000 : Date.now() + 45_000;
    reportSystemIncomingCall(state.incoming.id, displayName, expiresAt).catch(() => {});
    const pending = pendingSystemActionRef.current;
    if (pending?.callId === state.incoming.id) {
      pendingSystemActionRef.current = null;
      if (pending.action === 'answer') void accept(true);
      else if (pending.action === 'decline') void decline();
    }
  }, [state.phase, state.incoming, accept, decline]);

  const toggleMute = useCallback(() => {
    setState((s) => {
      const muted = !s.muted;
      mediaRef.current?.setMuted(muted);
      return { ...s, muted };
    });
  }, []);

  // Kamerayı kapatmak track'i kaldırmaz, yalnız susturur: kaldırmak yeniden
  // pazarlık gerektirir ve karşı tarafta çağrı kopmuş gibi görünür (9.4.14).
  const toggleCamera = useCallback(() => {
    setState((s) => {
      if (!s.video) return s;
      const cameraOff = !s.cameraOff;
      mediaRef.current?.setCameraEnabled(!cameraOff);
      return { ...s, cameraOff };
    });
  }, []);

  const switchCamera = useCallback(() => {
    mediaRef.current?.switchCamera();
  }, []);

  const selectAudioRoute = useCallback(async (route: AudioRoute) => {
    try {
      const result = await selectCallAudioRoute(route);
      // D-418 — izin reddi sessizce yutulmaz. `unavailable` beklenen bir
      // durumdur (yerli modül yok, düğme zaten çizilmez); izin reddi ise
      // kullanıcının GÖRDÜĞÜ bir eylemin sonucudur ve karşılığında bir şey
      // olmalıdır.
      if (!result.ok) {
        if (result.reason === 'bluetooth_permission') {
          setState((s) => ({ ...s, bluetoothPermissionDenied: true }));
        }
        return;
      }
      const next = result.state;
      setState((s) => ({
        ...s,
        audioRoute: next.current,
        audioRoutes: next.available,
        audioRouteError: false,
        bluetoothPermissionDenied: false,
      }));
    } catch {
      setState((s) => ({ ...s, audioRouteError: true }));
    }
  }, []);

  // Kablolu/Bluetooth aygıt takılıp çıkarıldığında kontrol bayat kalmasın.
  // Native katman gerçek rotayı bildirir; görünür seçim tahmin edilmez.
  useEffect(() => observeCallAudioRoutes((route) => {
    setState((s) => s.phase === 'outgoing' || s.phase === 'active'
      ? {
        ...s,
        audioRoute: route.current,
        audioRoutes: route.available,
        audioRoutingAvailable: true,
        audioRouteError: false,
        bluetoothPermissionDenied: false,
      }
      : s);
  }), []);

  // ── Aktif/giden çağrının durumunu yokla ────────────────────────────────
  useEffect(() => {
    if (!token || (state.phase !== 'outgoing' && state.phase !== 'active')) return;
    const id = state.call?.id;
    if (!id) return;
    const delay = callStatusPollDelay(state.phase);
    if (delay === null) return;
    let stop = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      if (stop) return;
      try {
        const call = await api.getCall(token, id);
        if (stop) return;
        if (call.status === 'accepted') {
          // Arayan tarafta cevap ilk kez geldiğinde medyaya bağlanır.
          if (state.phase === 'outgoing' && call.answer_sdp) {
            await mediaRef.current?.setAnswer(call.answer_sdp);
          }
          setState((s) => ({
            ...s,
            phase: 'active',
            call,
            elapsed: call.elapsed_seconds ?? s.elapsed,
            remaining: call.remaining_seconds,
            endingSoon: call.ending_soon,
            media: mediaStateRef.current,
          }));
          // 9.4.4.5 — bekleyen yeniden anlaşma turunu uygula. ARANAN yeni offer'ı
          // uygulayıp cevabı gönderir; ARAYAN gelen cevabı bağlar. `renegSeqRef`
          // aynı turu iki kez uygulamayı önler.
          const seq = call.renegotiation_seq ?? 0;
          if (seq > renegSeqRef.current && mediaRef.current) {
            if (roleRef.current === 'callee' && call.renegotiation_offer_sdp) {
              renegSeqRef.current = seq;
              try {
                const answer = await mediaRef.current.acceptOffer(call.renegotiation_offer_sdp);
                await api.answerCallRenegotiation(token, id, answer);
              } catch { /* yut — kurtarılamazsa grace dolunca çağrı kapanır */ }
            } else if (roleRef.current === 'caller' && call.renegotiation_answer_sdp) {
              renegSeqRef.current = seq;
              try { await mediaRef.current.setAnswer(call.renegotiation_answer_sdp); } catch { /* yut */ }
            }
          }
        } else if (call.status !== 'ringing') {
          // declined / missed / cancelled / ended — karşı taraf kapattı.
          teardown();
          setState((s) => ({
            ...s,
            phase: 'ended',
            call,
            errorTranslationKey: call.end_reason === 'media_not_connected'
              ? 'call.mediaConnectionFailed'
              : s.errorTranslationKey,
          }));
        }
      } catch { /* ağ hatası çağrıyı düşürmez, bir sonraki yoklama dener */ }
      finally {
        // setInterval yerine istek bittikten sonra planla: yavaş ağda iki durum
        // isteğinin üst üste binmesi ve hız limitini gereksiz tüketmesi önlenir.
        if (!stop) timer = setTimeout(() => { void tick(); }, delay);
      }
    };
    void tick();
    return () => {
      stop = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [token, state.phase, state.call?.id, teardown]);

  // Kabul edilmiş çağrının ilk ses yolu ayrı bir yaşam evresidir. Henüz bir
  // kez bile bağlanmadıysa başlangıcı ölç; bağlantı gelirse beklemeyi ücretten
  // düş, gelmezse aşağıdaki kesin 15 saniyelik sınır çağrıyı kapatır.
  useEffect(() => {
    const id = state.call?.id ?? null;
    if (!token || state.phase !== 'active' || id === null) return;
    if (initialMediaCallRef.current !== id) {
      initialMediaCallRef.current = id;
      initialMediaWaitStartRef.current = mediaEverConnectedRef.current
        ? null
        : Date.now() - Math.max(0, state.elapsed) * 1000;
    }
    if (state.media !== 'connected' || initialMediaWaitStartRef.current === null) return;
    const startedAt = initialMediaWaitStartRef.current;
    initialMediaWaitStartRef.current = null;
    const seconds = Math.round((Date.now() - startedAt) / 1000);
    if (seconds >= 1) {
      interruptionTotalRef.current += seconds;
      api.reportCallInterruption(token, id, interruptionTotalRef.current).catch(() => {});
    }
  }, [token, state.phase, state.call?.id, state.elapsed, state.media]);

  useEffect(() => {
    if (state.phase !== 'active' || mediaEverConnectedRef.current) return;
    const startedAt = initialMediaWaitStartRef.current;
    if (startedAt === null) return;
    const h = setTimeout(() => {
      if (!mediaEverConnectedRef.current) void finishCall('media_not_connected');
    }, initialMediaConnectDelay(startedAt));
    return () => clearTimeout(h);
  }, [state.phase, state.call?.id, state.media, finishCall]);

  // Ekrandaki sayaç: yoklamalar arasını yumuşatır. Ücretin kaynağı bu değil,
  // sunucudaki accepted_at ve bildirilen kopukluklardır (9.4.3.1). Medya
  // kesikken hizmet verilmediği için hem akan tutar hem kalan süre durur.
  useEffect(() => {
    if (state.phase !== 'active') return;
    const h = setInterval(() => {
      setState(advanceVisibleCallClock);
    }, 1000);
    return () => clearInterval(h);
  }, [state.phase]);

  // Rezervasyon tükendi: sunucu da kesiyor (9.4.3.3), istemci beklemeden kapatır
  // ki kullanıcı sessiz bir hatta kalmasın.
  useEffect(() => {
    if (state.phase === 'active' && state.remaining === 0) { void hangUp(); }
  }, [state.phase, state.remaining, hangUp]);

  // 9.4.4.5 — kurtarma denemesi. Medya kopunca ARAYAN, grace dolmadan bir
  // ICE-restart turu başlatır: yeni offer üretip sunucuya koyar; aranan cevabı
  // yoklamada uygular ve ağ yolu tazelenir. Yalnız arayan başlatır (tek offerer,
  // glare yok). Bu kopukluk episode'u boyunca (state.media değişene kadar) tek
  // deneme; kurtulmazsa aşağıdaki grace çağrıyı kapatır.
  useEffect(() => {
    if (state.phase !== 'active' || roleRef.current !== 'caller') return;
    if (state.media !== 'interrupted' && state.media !== 'failed') return;
    let stop = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    // Zincirleme: her deneme bittiğinde bir sonrakini kurar. Bağlantı dönerse
    // state.media değişir, efekt yeniden kurulur ve temizleyici zinciri keser.
    const schedule = () => {
      if (stop || attempt >= MEDIA_RECONNECT_DELAYS_MS.length) return;
      const delay = MEDIA_RECONNECT_DELAYS_MS[attempt];
      attempt += 1;
      timer = setTimeout(async () => {
        const id = activeId.current;
        if (stop || !mediaRef.current || id === null || !token) return;
        try {
          const offer = await mediaRef.current.restartIce();
          await api.renegotiateCall(token, id, offer);
        } catch { /* yut — sonraki deneme ya da grace kapatır */ }
        schedule();
      }, delay);
    };
    schedule();
    return () => { stop = true; if (timer) clearTimeout(timer); };
  }, [state.phase, state.media, token]);

  // 9.4.4.5 — kopukluk eşiği. Medya kesildiğinde sunucu bunu göremez ve ücret
  // duvar saatinden işlemeye devam eder; çağrıyı bitirmek istemcinin işi. Eşik
  // 30 sn: wifi↔hücresel geçişi birkaç saniye sürebildiği için çağrıyı hemen
  // düşürmek yanlış olurdu, ama sınırsız beklemek sessizliği faturalandırır.
  useEffect(() => {
    if (state.phase !== 'active') return;
    if (state.media !== 'interrupted' && state.media !== 'failed') return;
    const h = setTimeout(() => { void hangUp(); }, MEDIA_INTERRUPT_GRACE_MS);
    return () => clearTimeout(h);
  }, [state.phase, state.media, hangUp]);

  // D-102 — kopukluk saatini başlat/durdur. Çağrı sürerken medya kesilirse
  // sayaç başlar; bağlantı geri dönerse geçen süre bildirilir ve ücretten
  // düşülür. Çağrı kopukken biterse bildirimi hangUp yapıyor.
  useEffect(() => {
    if (state.phase !== 'active') return;
    if (!mediaEverConnectedRef.current) return;
    const kopuk = state.media === 'interrupted' || state.media === 'failed';
    if (kopuk && kopukBasi.current === null) {
      kopukBasi.current = Date.now();
    } else if (!kopuk && kopukBasi.current !== null && state.media === 'connected') {
      void bildirKopukluk();
    }
  }, [state.phase, state.media, bildirKopukluk]);

  return {
    ...state,
    start,
    accept,
    decline,
    hangUp,
    toggleMute,
    toggleCamera,
    switchCamera,
    selectAudioRoute,
    refreshIncoming,
    reset,
  };
}
